import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';
import {
  Box, Heading, Select, Flex, useToast,
} from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

import GenericTable from '../components/GenericTable';
import { getGameWiseSaleReport } from '../actions/gameWiseReportingActions';
import { fetchCompanies } from '../actions/companyActions';
import CompanySelect from '../components/CompanySelect';
import { getLocalDateRangeForPlayerWise } from '../utils/reportDateRange';
import { useFieldPrivilege } from '../hooks/useFieldPrivilege';

const base_url = process.env.REACT_APP_API_URL_ADMIN;
const REFRESH_INTERVAL_MS = 60000;

const filterOptions = [
  'Today',
  'Yesterday',
  'This Week',
  'Last Week',
  'This Month',
  'Custom Range',
];

const GameWiseSaleReport = () => {
  const dispatch = useDispatch();
  const toast = useToast();
  const socketRef = useRef(null);
  const { filterColumns } = useFieldPrivilege();

  const { data, loading, error } = useSelector((state) => state.gameWiseSaleReport);
  const user = useSelector((state) => state.auth.user);
  const companies = useSelector((state) => state.company?.companies ?? []);

  const isSuperAdmin = user?.role === 'super_admin';

  const [filter, setFilter] = useState('Today');
  const [customRange, setCustomRange] = useState([null, null]);
  const [companyId, setCompanyId] = useState('');

  const [startDate, endDate] = useMemo(() => {
    return filter === 'Custom Range' ? customRange : getLocalDateRangeForPlayerWise(filter);
  }, [filter, customRange]);

  useEffect(() => {
    if (isSuperAdmin) dispatch(fetchCompanies());
  }, [dispatch, isSuperAdmin]);

  // Non–super-admin: use company from login (no company.view privilege needed).
  useEffect(() => {
    if (isSuperAdmin) return;
    const id = user?.companyId || user?.company_id;
    if (id) setCompanyId(String(id));
  }, [isSuperAdmin, user?.companyId, user?.company_id]);

  useEffect(() => {
    if (isSuperAdmin || !user?.companySlug || !companies.length) return;
    const match = companies.find((c) => c.slug === user.companySlug);
    if (match?._id) setCompanyId(String(match._id));
  }, [isSuperAdmin, user?.companySlug, companies]);

  useEffect(() => {
    if (!startDate || !endDate || !companyId) return;
    dispatch(getGameWiseSaleReport({ startDate, endDate, companyId }));
  }, [dispatch, startDate, endDate, companyId]);

  const refreshData = useCallback((silent = false) => {
    if (startDate && endDate && companyId) {
      dispatch(getGameWiseSaleReport({ startDate, endDate, companyId, silent }));
    }
  }, [dispatch, startDate, endDate, companyId]);

  useEffect(() => {
    socketRef.current = io(base_url, { withCredentials: true });
    return () => {
      socketRef.current && socketRef.current.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!startDate || !endDate || !companyId) return;
    const interval = setInterval(() => refreshData(true), REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [startDate, endDate, companyId, refreshData]);

  useEffect(() => {
    const handleUpdate = () => refreshData(true);
    if (socketRef.current) {
      socketRef.current.off('reportUpdate');
      socketRef.current.on('reportUpdate', handleUpdate);
    }
    return () => {
      socketRef.current && socketRef.current.off('reportUpdate');
    };
  }, [refreshData]);

  const columns = useMemo(
    () => [
      { Header: 'Game Name', accessor: 'game_name' },
      { Header: 'Sales', accessor: 'sales' },
      { Header: 'Claimed', accessor: 'claimed' },
      { Header: 'UnClaim', accessor: 'unclaimed' },
      { Header: 'Cancelled', accessor: 'cancelledAmount' },
      { Header: 'Cancelled Count', accessor: 'cancelledCount' },      
      { Header: 'Profit', accessor: 'profit' },
    ],
    []
  );

  const visibleColumns = useMemo(
    () => filterColumns('reportGameWise', columns),
    [columns, filterColumns]
  );

  return (
    <Box>
      <Heading mb={4}>Game-Wise Sale Report</Heading>

      <Flex gap={4} mb={4} align="center">
        {isSuperAdmin && (
          <CompanySelect value={companyId} onChange={setCompanyId} />
        )}
        <Select
          width="200px"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        >
          {filterOptions.map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </Select>

        {filter === 'Custom Range' && (
          <>
            <DatePicker
              selected={customRange[0]}
              onChange={(date) => setCustomRange([date, customRange[1]])}
              placeholderText="Start Date"
              selectsStart
              startDate={customRange[0]}
              endDate={customRange[1]}
            />
            <DatePicker
              selected={customRange[1]}
              onChange={(date) => setCustomRange([customRange[0], date])}
              placeholderText="End Date"
              selectsEnd
              startDate={customRange[0]}
              endDate={customRange[1]}
              minDate={customRange[0]}
            />
          </>
        )}
      </Flex>

      {loading ? (
        <Box>Loading...</Box>
      ) : error ? (
        <Box>Error: {error}</Box>
      ) : !companyId ? (
        <Box>
          {isSuperAdmin
            ? 'Please select a company to view this report.'
            : 'Unable to resolve your company. Please sign in again.'}
        </Box>
      ) : Array.isArray(data) && data.length > 0 ? (
        <GenericTable columns={visibleColumns} data={data} compactOnMobile />
      ) : (
        <Box>No data available for this period.</Box>
      )}
    </Box>
  );
};

export default GameWiseSaleReport;
